import { Component } from '../core/component.js';
import { MapUtils } from '../../utility/map-utils.js';
import { LegacyPaymentSettings } from '../../models/serializable/legacy-payment-settings.js';
import {
  DataModelBuilder,
  isSameData,
  SerializationContext,
  SerializedData,
} from '../../models/dto/dto.js';
import { toEnumFromString } from '../../serialization/enum-utils.js';
import { VersionRange } from '../../models/core/version-range.js';
import { PaymentAccountRef } from '../../models/serializable/payment-account-ref.js';
import { PaymentType } from '../../models/serializable/payment-type.js';

/**
 * @deprecated Use {@link PaymentProtocolComponent} instead.
 */
export class LegacyPaymentSettingsComponent extends Component {
  static readonly TYPE_ID: string = '136fbd9410324b2da7d24e2898be08b9';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly settings: LegacyPaymentSettings,
  ) {
    super();
  }

  get $typeId() {
    return LegacyPaymentSettingsComponent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(LegacyPaymentSettingsComponent.SCHEMA_VERSION);
  }

  get $removable() {
    return false;
  }

  static fromMap(context: SerializationContext, map: SerializedData): LegacyPaymentSettingsComponent {
    return new LegacyPaymentSettingsComponent(
        context.fromData(MapUtils.get<SerializedData>(map, 'settings')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'settings': context.toData(this.settings),
    };
  }

  clone(): this {
    return new LegacyPaymentSettingsComponent(
        this.settings.clone(),
    ) as this;
  }

  isSame(other: LegacyPaymentSettingsComponent): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.settings, this.settings);
  }
}

export class LegacyPaymentSettingsComponentBuilder extends DataModelBuilder<LegacyPaymentSettingsComponent> {
  build(context: SerializationContext, map: SerializedData): LegacyPaymentSettingsComponent {
    return LegacyPaymentSettingsComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(
      context: SerializationContext,
      map: SerializedData,
  ): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(LegacyPaymentSettingsComponent.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(LegacyPaymentSettingsComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    /** Version 2:
     * - parameters `paymentAccount` of type `Optional<PaymentAccountRef>` and
     *   `paymentMethods` of type `List<PaymentType>` were replaced with
     *   parameter `settings` of type `LegacyPaymentSettings`
     * - note: before 2026.01.26. the default value of parameter
     *   `settings.allowOfflineOrderReceipts` was `true`
     */
    if(version < 2) {
      const paymentAccount = context.fromDataOrNull<PaymentAccountRef>(
          MapUtils.getOrNull<SerializedData>(map, 'paymentAccount'),
      );

      const paymentMethods = MapUtils.getList<PaymentType>(
          map,
          'paymentMethods',
          (value) => toEnumFromString(value as string, PaymentType) ?? PaymentType.OnDelivery
      );

      const paymentSettings = LegacyPaymentSettings.create({
        paymentAccount: paymentAccount,
        paymentMethods: paymentMethods,
        allowLegalEntityInvoices: false,
        allowOfflineOrderReceipts: false,
      });

      MapUtils.setIfNullOrUndefined(map, 'settings', context.toData(paymentSettings));

      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
